import {
  useContext,
  memo,
  useMemo,
  useEffect,
} from "react";
import { ChartContainerSizeContext, XAxisYear, YAxisPercentage, YAxisGridlines } from "@atlas-int/viz/shared";
import styled from "@emotion/styled";
import { text, surface, fontSize, spacing } from "@atlas-int/tokens";
import { curveMonotoneX, groups, line } from "d3";
import { dataVizLayout } from "@atlas-int/tokens";
const defaultChartMargin = dataVizLayout.defaultChartMargin;
import { ProductLevel, ProductClass } from "@atlas-int/data/types";
import { getClassificationNamesMap, ProductClass as PC, UIView, useProductYearMetadata } from "@atlas-int/data";
import { getColorMap, getNamesMap } from "@atlas-int/viz";
import transformProducts from "./transformProducts";
import MarketShareLineGroup from "./MarketShareLine";
import { determineClosestXValue } from "@atlas-int/viz/shared";
import { removeOtherProducts } from "./utils";
import { useDownload } from "@atlas-int/viz";
import type { ScaleLinear } from "d3-scale";
import { useChartScales } from "./useChartScales";
import type { MarketShareDataPoint, MarketShareChartProps } from "./types";

const Root = styled.div`
  position: relative;
  width: 100%;
  height: 100%;
  margin: ${spacing[0]};
  padding: ${spacing[0]};

  & svg {
    margin: ${spacing[0]};
    padding: ${spacing[0]};
  }
`;

const YAxisLabel = styled.div`
  height: 100%;
  width: ${defaultChartMargin.left / 2}px;
  position: absolute;
  top: 0px;
  left: 0px;
  padding-top: ${defaultChartMargin.bottom}px;
  display: flex;
  flex-direction: column;
  justify-content: center;
  text-align: center;
  font-size: ${fontSize.lg};
  color: ${text.primary};
  box-sizing: border-box;
  pointer-events: none;

  writing-mode: vertical-lr;
  -webkit-transform: rotate(-180deg);
  -moz-transform: rotate(-180deg);
  transform: rotate(-180deg);
`;

const Chart = ({
  inputData,
  queryVariables,
  tradeDirection: _tradeDirection,
  hiddenCategories,
  setXAxisTooltipValue,
  handleTooltip,
  isTooltipFixed,
  chartMargin = defaultChartMargin,
}: MarketShareChartProps) => {
  const { dataRef } = useDownload();
  const { chartWidth, chartHeight } = useContext(ChartContainerSizeContext);
  const { exporter, productClass, yearMin, yearMax, servicesClass } = queryVariables;

  const { svgRef } = useDownload();

  const { xScale, yScale, setYAxisDomain } = useChartScales({
    yearMin,
    yearMax,
    chartWidth,
    chartHeight,
    chartMargin,
  });

  const useColorMap = useMemo(
    () => getColorMap({ view: UIView.Products, productClass }),
    [productClass],
  );
  let noDataToDisplay: boolean = false;
  const productYearState = useProductYearMetadata({
    yearRange: { yearMin, yearMax },
    productClass,
    productLevel: ProductLevel.ProductSection,
    servicesClass,
  });
  const productYearMetadata = productYearState.status === "success" ? productYearState.data : undefined;
  const productYearLoading = productYearState.status === "loading";
  const productYearError = productYearState.status === "error" ? productYearState.error : undefined;

  let { data } = inputData;

  let filteredData = data
    .filter((d: Record<string, unknown>) => {
      const productId = d.productId as string;

      if (hiddenCategories.includes(productId)) {
        return false;
      } else {
        return true;
      }
    })
    .filter(removeOtherProducts);

  let transformedData: MarketShareDataPoint[] | undefined = undefined;
  let groupedBySectorData: [string, MarketShareDataPoint[]][] | undefined = undefined;

  if (!productYearLoading && !productYearError && productYearMetadata) {
    transformedData = transformProducts({
      exporter,
      inputData: filteredData as { year: number; productId: string; exportValue: number; [key: string]: unknown }[],
      productYearData: productYearMetadata as unknown as { groupYear: string; year: number; groupData: { productId: string; exportValue: number; [key: string]: unknown }[]; [key: string]: unknown }[],
    });

    if (transformedData) {
      let maxGlobalShareValue = Math.max(
        ...transformedData.map(
          ({ globalMarketShare }: MarketShareDataPoint) => globalMarketShare,
        ),
      );

      if (maxGlobalShareValue) {
        setYAxisDomain({ min: 0, max: maxGlobalShareValue });
      }

      groupedBySectorData = groups(
        transformedData,
        ({ productId }: MarketShareDataPoint) => productId,
      );
    } else {
      noDataToDisplay = true;
    }
  }

  const marketLineGenerator = useMemo(() => {
    return ({ x, y }: { x: ScaleLinear<number, number>; y: ScaleLinear<number, number> }) => {
      return line<MarketShareDataPoint>()
        .x((d) => x(d.year))
        .y((d) => y(d.globalMarketShare))
        .defined((d) => d.year != null && d.globalMarketShare != null)
        .curve(curveMonotoneX);
    };
  }, []);

  const lineGenerator = useMemo(
    () => marketLineGenerator({ x: xScale, y: yScale }),
    [marketLineGenerator, xScale, yScale],
  );

  const pointGenerator = useMemo(() => {
    return ({ year, value }: { year: number; value: number }) => {
      return { cx: xScale(year), cy: yScale(value) };
    };
  }, [xScale, yScale]);

  useEffect(() => {
    const productNamesMap = getNamesMap({ productClass }) ?? getClassificationNamesMap(PC.HS92Products);

    dataRef.current = transformedData?.map((d: MarketShareDataPoint) => ({
      Sector: productNamesMap.get(d.productId) || d.productId,
      Year: d.year,
      "Market Share": d.globalMarketShare,
    }));
  }, [transformedData, dataRef, productClass]);
  let contentToRender: React.ReactNode = null;
  if (transformedData === undefined && noDataToDisplay === true) {
    contentToRender = null;
  } else {
    contentToRender = (
      <>
        <YAxisLabel data-download-exclude>Share of World Market by Sector</YAxisLabel>
        <svg
          role="img"
          aria-label="Market share over time chart"
          style={{ backgroundColor: surface.white }}
          width={chartWidth}
          height={chartHeight}
          onMouseMove={(e) => {
            if (isTooltipFixed) {
              return;
            }
            if (!transformedData || !Array.isArray(transformedData)) {
              setXAxisTooltipValue(null);
              handleTooltip({ data: [] });
              return;
            }

            determineClosestXValue({
              e,
              xScale,
              containerSize: { width: chartWidth, height: chartHeight },
              chartMargin,
              setXAxisTooltipValue,
            });

            const validData = transformedData.filter((d) => {
              return (
                d &&
                typeof d === "object" &&
                "year" in d &&
                d.year != null &&
                d.globalMarketShare !== undefined &&
                d.globalMarketShare !== null
              );
            });

            handleTooltip({ data: validData });
          }}
          onMouseLeave={() => {
            if (isTooltipFixed) {
              return;
            }
            setXAxisTooltipValue(null);
            handleTooltip({ data: [] });
          }}
          ref={svgRef}
        >
          <YAxisGridlines
            scale={yScale}
            containerSize={{ width: chartWidth, height: chartHeight }}
            chartMargin={chartMargin}
          />

          {groupedBySectorData &&
            groupedBySectorData
              .filter((groupedBySector: [string, MarketShareDataPoint[]]) => {
                // Only render lines for sectors that have at least one valid data point
                let [_sectorProductId, sectorYearData] = groupedBySector;
                return sectorYearData.some(
                  (d: MarketShareDataPoint) =>
                    d.globalMarketShare !== undefined &&
                    d.globalMarketShare !== null,
                );
              })
              .map((groupedBySector: [string, MarketShareDataPoint[]]) => {
                let [sectorProductId, sectorYearData] = groupedBySector;
                let lineKeyString = `${exporter}_${productClass}_${sectorProductId}`;
                return (
                  <MarketShareLineGroup
                    key={lineKeyString}
                    sectorProductId={sectorProductId}
                    lineData={sectorYearData}
                    lineGenerator={lineGenerator}
                    pointGenerator={pointGenerator}
                    colorMap={useColorMap}
                  />
                );
              })}

          <XAxisYear
            scale={xScale}
            containerSize={{ width: chartWidth, height: chartHeight }}
            chartMargin={chartMargin}
          />
          <YAxisPercentage
            scale={yScale}
          />
        </svg>
      </>
    );
  }

  return <Root>{contentToRender}</Root>;
};

export default memo(Chart);
